import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Assignment as ReportIcon } from '@mui/icons-material';
import { useAuditReportStatus, useGenerateReportFromAudit } from '../hooks/useAuditReport';

interface GenerateReportButtonProps {
  auditId: string;
  auditStatus: string;
  isLocked: boolean;
  surveyTitle?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
}

export const GenerateReportButton: React.FC<GenerateReportButtonProps> = ({
  auditId,
  auditStatus,
  isLocked,
  surveyTitle,
  size = 'medium',
  variant = 'contained',
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportTitle, setReportTitle] = useState(
    surveyTitle ? `${surveyTitle} - Audit Report` : 'Survey Audit Report'
  );
  const [reportDescription, setReportDescription] = useState(
    'Automatically generated report from published audit'
  );

  const { shouldShowGenerateButton, isLoading } = useAuditReportStatus(
    auditId,
    auditStatus,
    isLocked
  );

  const generateReport = useGenerateReportFromAudit();

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleGenerateReport = async () => {
    try {
      await generateReport.mutateAsync({
        auditId,
        data: {
          title: reportTitle.trim() || undefined,
          description: reportDescription.trim() || undefined,
        },
      });
      handleCloseDialog();
    } catch (error) {
      // Error is handled by the hook's onError callback
      console.error('Failed to generate report:', error);
    }
  };

  // Don't render if we shouldn't show the button
  if (!shouldShowGenerateButton) {
    return null;
  }

  return (
    <>
      <Button
        variant={variant}
        color="secondary"
        size={size}
        startIcon={isLoading ? <CircularProgress size={16} /> : <ReportIcon />}
        onClick={handleOpenDialog}
        disabled={isLoading || generateReport.isPending}
      >
        Generate Report
      </Button>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Survey Report</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            This audit has been published but no report has been generated yet. You can generate a
            report now with custom title and description.
          </DialogContentText>

          <Alert severity="info" sx={{ mb: 3 }}>
            The report will include all responses and comments that were included during the audit
            process.
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Report Title"
              fullWidth
              value={reportTitle}
              onChange={(e) => setReportTitle(e.target.value)}
              placeholder="Enter a title for the report"
              helperText="Leave empty to use default title"
            />

            <TextField
              label="Report Description"
              fullWidth
              multiline
              rows={3}
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              placeholder="Enter a description for the report"
              helperText="Optional description explaining the context of this report"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={generateReport.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerateReport}
            variant="contained"
            color="secondary"
            disabled={generateReport.isPending}
            startIcon={generateReport.isPending ? <CircularProgress size={16} /> : <ReportIcon />}
          >
            {generateReport.isPending ? 'Generating...' : 'Generate Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
